import type { TFunction } from 'i18next';
import numeral from 'numeral';

import type { ISliderProps } from '@feathr/components';

// Actually 10,000,000 after logarithmic scale
export const SLIDER_MAX_VALUE = 100;
// Actually 10,000 after logarithmic scale
export const SLIDER_MIN_VALUE = 0;
export const SLIDER_DEFAULT_VALUE = SLIDER_MIN_VALUE;

const SLIDER_ACTUAL_MAX_VALUE = 10_000_000;
const SLIDER_ACTUAL_MIN_VALUE = 10_000;

/** Helper to convert the slider position to the value */
export function sliderToValue(sliderPosition: number): number {
  const logMin = Math.log10(SLIDER_ACTUAL_MIN_VALUE);
  const logMax = Math.log10(SLIDER_ACTUAL_MAX_VALUE);
  const logValue = logMin + (sliderPosition / 100) * (logMax - logMin);
  return Math.round(10 ** logValue);
}

/** Helper to convert the value to the slider position */
export function valueToSlider(actualValue: number): number {
  const logMin = Math.log10(SLIDER_ACTUAL_MIN_VALUE);
  const logMax = Math.log10(SLIDER_ACTUAL_MAX_VALUE);
  const logValue = Math.log10(actualValue);
  return Math.round(((logValue - logMin) / (logMax - logMin)) * 100);
}

/** Helper to round the value to the nearest 1k, 10k, or 100k */
export function roundValue(value: number): number {
  if (value < 100_000) {
    return Math.round(value / 1_000) * 1_000;
  } else if (value < 1_000_000) {
    return Math.round(value / SLIDER_ACTUAL_MIN_VALUE) * SLIDER_ACTUAL_MIN_VALUE;
  } else {
    return Math.round(value / 100_000) * 100_000;
  }
}

export function onFormatTickLabel(sliderPosition: number): string {
  const actualValue = sliderToValue(sliderPosition);
  const roundedValue = roundValue(actualValue);

  if (roundedValue >= 1_000_000) {
    // Shows 4.1m
    return numeral(roundedValue).format('0.0a');
  }
  // Shows 100k
  return numeral(roundedValue).format('0a');
}

export function getSliderMarks(t: TFunction): ISliderProps['marks'] {
  const marks = [
    { value: SLIDER_MIN_VALUE, label: t('10,000') },
    { value: SLIDER_MAX_VALUE, label: t('10 million') },
  ];

  return marks;
}
